/**
 * Contracts with an airline or car company — list, add, edit, attach the PDF.
 *
 *   list     GET   /api/btp/vendor-contracts?vendor_uid=
 *   add      POST  /api/btp/vendor-contracts                 { vendor_uid, title, … }
 *   edit     PATCH /api/btp/vendor-contracts                 { id, …changed fields }
 *   upload   POST  /api/btp/vendor-contracts/upload          → { upload_url, blob_path }
 *            then  PUT bytes → upload_url (the file never touches our server)
 *   open     GET   /api/btp/vendor-contracts/:id/download    → { url }
 *
 * What AirPortal and SharePoint hold today: a corporate discount for one customer,
 * or a Christopherson-wide (TMC-level) deal nobody owns alone. "Christopherson
 * wide" is an explicit choice in the form rather than an empty customer box, so a
 * deal is never recorded as company-wide because someone forgot to pick.
 *
 * No delete, and no Remove button. A contract that ended is Expired or
 * Terminated — the record and its document are the evidence of what was agreed,
 * and the server has no delete route to call.
 *
 * Writes need write:supplier; a read-only account gets the list, the
 * documents, each contract's terms and notes as text under its row, and a
 * sentence saying why there is no Add button. The server gate is
 * the real one.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { fmtCalendarDate } from '@/features/crm/lib/dates';
import { CustomerSearchSelect } from './CustomerSearchSelect';

export interface VendorContract {
  id: string;
  vendor_uid: string;
  customer_id: string | null;
  customer_name: string | null;
  contract_type: 'corporate' | 'tmc' | 'other';
  title: string;
  program_code: string | null;
  discount_pct: string | null;
  terms: string | null;
  start_date: string | null;
  end_date: string | null;
  status: 'pending' | 'active' | 'expired' | 'terminated';
  has_document: boolean;
  /**
   * DERIVED by the server from end_date on every read (vendor-view.ts
   * contractStanding). Optional so an older response still renders.
   *   lapsed         pending or active, but the end date has passed
   *   expiring_soon  active, ends within the 120-day renewal window
   */
  days_to_end?: number | null;
  lapsed?: boolean;
  expiring_soon?: boolean;
  notes: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string | null;
}

interface ContractsResponse {
  contracts: VendorContract[];
  truncated?: boolean;
  limit?: number;
}

export const CONTRACT_TYPE_LABELS: Record<
  VendorContract['contract_type'],
  string
> = {
  corporate: 'Corporate',
  tmc: 'TMC',
  other: 'Other',
};

export const CONTRACT_STATUS_LABELS: Record<VendorContract['status'], string> =
  {
    pending: 'Pending',
    active: 'Active',
    expired: 'Expired',
    terminated: 'Terminated',
  };

const STATUS_BADGE: Record<VendorContract['status'], string> = {
  pending: 'badge-blue',
  active: 'badge-green',
  expired: 'badge-amber',
  terminated: 'badge-red',
};

/** Matches the server's MAX_FILE_SIZE_BYTES; checked here so the user is told before uploading. */
const MAX_FILE_BYTES = 50 * 1024 * 1024;

export const CHRISTOPHERSON_WIDE = 'Christopherson wide';

/**
 * Header cells in the table a read-only account sees (no Edit column) — what the
 * terms-and-notes row spans. The spec checks it against the rendered header.
 */
const READ_ONLY_COLUMNS = 8;

const day = (iso: string | null) => (iso ? (fmtCalendarDate(iso) ?? iso) : '—');

function term(c: Pick<VendorContract, 'start_date' | 'end_date'>): string {
  if (!c.start_date && !c.end_date) return 'No term dates';
  return `${day(c.start_date)} → ${day(c.end_date)}`;
}

/**
 * The status chip, plus what the dates say. A lapsed contract shows as
 * "End date passed" in place of its stale status, so nobody reads a deal that
 * ended in June as Active; the stored status is kept in the tooltip.
 */
function StatusCell({ c, testId }: { c: VendorContract; testId: string }) {
  const label = CONTRACT_STATUS_LABELS[c.status] ?? c.status;
  if (c.lapsed) {
    return (
      <span
        className="badge badge-no-dot badge-amber"
        data-testid={`${testId}-status-${c.id}`}
        title={`Marked ${label}, but the end date has passed. Edit it to Expired or update the dates.`}
      >
        End date passed
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      <span
        className={`badge badge-no-dot ${STATUS_BADGE[c.status] ?? ''}`}
        data-testid={`${testId}-status-${c.id}`}
      >
        {label}
      </span>
      {c.expiring_soon && c.days_to_end != null && (
        <span
          className="badge badge-no-dot badge-amber"
          data-testid={`${testId}-expiring-${c.id}`}
          title="Inside the 120-day renewal window"
        >
          {c.days_to_end === 0 ? 'Ends today' : `Ends in ${c.days_to_end} days`}
        </span>
      )}
    </span>
  );
}

export function VendorContracts({
  vendorUid,
  testId = 'vendor-contracts',
  initialContracts,
}: {
  vendorUid: string;
  testId?: string;
  /**
   * The first load, when the vendor page fetched it with the vendor — so this
   * section renders in the same commit as the header. See
   * SupplierContactsProps.initialContacts for why that matters.
   */
  initialContracts?: VendorContract[];
}) {
  const { can } = useMe();
  const canWrite = can('write:supplier');

  const [contracts, setContracts] = useState<VendorContract[] | null>(
    initialContracts ?? null,
  );
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(initialContracts === undefined);
  const seeded = useRef(initialContracts !== undefined);
  const [error, setError] = useState<string | null>(null);
  /** Which form is open: 'new', a contract id, or none. */
  const [editing, setEditing] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<ContractsResponse>(
      `/api/btp/vendor-contracts?vendor_uid=${encodeURIComponent(vendorUid)}`,
    )
      .then((d) => {
        if (!cancelled) {
          setContracts(d.contracts);
          setTruncated(Boolean(d.truncated));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          // An error, not an empty list: "no contracts" is a claim about the
          // vendor, a failed request is a claim about us.
          setError(e instanceof ApiError ? e.message : String(e));
          setContracts(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorUid]);

  useEffect(() => {
    if (seeded.current) {
      seeded.current = false;
      return;
    }
    return load();
  }, [load]);

  const afterSave = () => {
    setEditing(null);
    load();
  };

  const openDoc = async (id: string) => {
    setDocError(null);
    // Opened synchronously inside the click so the popup blocker allows it, then
    // pointed at the signed URL once minted.
    //
    // NOT `window.open('', '_blank', 'noopener')`, which is what ContractsPanel
    // does: with `noopener` the call returns null by spec, so the code falls
    // through to its second window.open and the reader gets a blank tab AND the
    // document tab. Opening without the feature and cutting `opener` by hand
    // gives the same protection with one tab. (Found in the local headless walk,
    // 2026-10-08; ContractsPanel is left for its own change.)
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const { url } = await apiFetch<{ url: string }>(
        `/api/btp/vendor-contracts/${encodeURIComponent(id)}/download`,
      );
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      tab?.close();
      setDocError(e instanceof ApiError ? e.message : String(e));
    }
  };

  if (loading && contracts === null) {
    return (
      <p className="detail-note" data-testid={`${testId}-loading`}>
        <span className="muted">Loading contracts…</span>
      </p>
    );
  }

  if (error) {
    return (
      <p className="detail-note" data-testid={`${testId}-error`}>
        <span className="muted">
          Contracts aren&rsquo;t available right now — this is a loading or
          connection problem, not an empty list. Reload the page to retry.
        </span>
      </p>
    );
  }

  const rows = contracts ?? [];

  return (
    <div data-testid={testId}>
      {docError && (
        <div
          className="banner banner-error"
          data-testid={`${testId}-doc-error`}
          role="alert"
          style={{ marginBottom: 10 }}
        >
          {docError}
        </div>
      )}

      {rows.length === 0 && (
        <p className="detail-note" data-testid={`${testId}-empty`}>
          <span className="muted">
            No contracts recorded for this vendor yet.
          </span>
        </p>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="detail-table" data-testid={`${testId}-table`}>
            <thead>
              <tr>
                <th>Contract</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Program code</th>
                <th className="right">Discount</th>
                <th>Term</th>
                <th>Status</th>
                <th>Document</th>
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <Fragment key={c.id}>
                  <tr data-testid={`${testId}-row-${c.id}`}>
                    <td style={{ fontWeight: 600 }}>{c.title}</td>
                    <td>
                      {c.customer_id ? (
                        (c.customer_name ?? c.customer_id)
                      ) : (
                        <span
                          className="badge badge-blue badge-no-dot"
                          title="A TMC-level deal that no single customer owns."
                        >
                          {CHRISTOPHERSON_WIDE}
                        </span>
                      )}
                    </td>
                    <td>
                      {CONTRACT_TYPE_LABELS[c.contract_type] ?? c.contract_type}
                    </td>
                    <td className="mono">{c.program_code ?? '—'}</td>
                    <td className="right">
                      {c.discount_pct != null ? `${c.discount_pct}%` : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{term(c)}</td>
                    <td>
                      <StatusCell c={c} testId={testId} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.has_document ? (
                        <button
                          className="btn btn-sm"
                          data-testid={`${testId}-open-${c.id}`}
                          onClick={() => void openDoc(c.id)}
                          type="button"
                        >
                          View PDF
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    {canWrite && (
                      <td className="right">
                        <button
                          className="btn btn-sm"
                          data-testid={`${testId}-edit-${c.id}`}
                          onClick={() =>
                            setEditing(editing === c.id ? null : c.id)
                          }
                          type="button"
                        >
                          {editing === c.id ? 'Cancel' : 'Edit'}
                        </button>
                      </td>
                    )}
                  </tr>
                  {!canWrite && (
                    <ContractDetailsRow
                      c={c}
                      colSpan={READ_ONLY_COLUMNS}
                      testId={testId}
                    />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {truncated && (
        <p className="detail-note" data-testid={`${testId}-truncated`}>
          <span className="muted">
            Showing the first {rows.length} contracts for this vendor — there
            are more than this list holds.
          </span>
        </p>
      )}

      {canWrite ? (
        <>
          {rows.map((c) =>
            editing === c.id ? (
              <ContractForm
                contract={c}
                key={`edit-${c.id}`}
                onCancel={() => setEditing(null)}
                onSaved={afterSave}
                testId={`${testId}-edit-form`}
                vendorUid={vendorUid}
              />
            ) : null,
          )}
          {editing === 'new' ? (
            <ContractForm
              onCancel={() => setEditing(null)}
              onSaved={afterSave}
              testId={`${testId}-add`}
              vendorUid={vendorUid}
            />
          ) : (
            <button
              className="btn btn-sm"
              data-testid={`${testId}-add-button`}
              onClick={() => setEditing('new')}
              style={{ marginTop: 10 }}
              type="button"
            >
              Add contract
            </button>
          )}
        </>
      ) : (
        <p className="detail-note" data-testid={`${testId}-readonly`}>
          <span className="muted">
            You have read-only access to supplier records, so adding or editing
            contracts isn&rsquo;t available on this account.
          </span>
        </p>
      )}
    </div>
  );
}

/**
 * Terms and notes, read-only, under a contract's row — for an account without
 * write:supplier. An editor reads and changes them in the form (Edit), which is
 * unchanged; before this, a viewer had no way to see them at all, so negotiated
 * terms recorded without a PDF were invisible to the people who only read them
 * (Codex review of #2168). Nothing renders when both are empty, so a contract
 * with no terms costs no blank row. pre-wrap keeps the line breaks typed into
 * the form's textarea.
 */
function ContractDetailsRow({
  c,
  colSpan,
  testId,
}: {
  c: VendorContract;
  colSpan: number;
  testId: string;
}) {
  const terms = c.terms?.trim() ?? '';
  const notes = c.notes?.trim() ?? '';
  if (!terms && !notes) return null;
  const label = (text: string) => (
    <span style={{ fontSize: 11, fontWeight: 600 }}>{text}</span>
  );
  return (
    <tr>
      <td colSpan={colSpan} style={{ paddingTop: 0 }}>
        <div
          data-testid={`${testId}-details-${c.id}`}
          style={{ display: 'grid', gap: 6 }}
        >
          {terms && (
            <div style={{ display: 'grid', gap: 3 }}>
              {label('Terms')}
              <span
                data-testid={`${testId}-terms-${c.id}`}
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {terms}
              </span>
            </div>
          )}
          {notes && (
            <div style={{ display: 'grid', gap: 3 }}>
              {label('Notes')}
              <span
                className="muted"
                data-testid={`${testId}-notes-${c.id}`}
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {notes}
              </span>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ── The add / edit form ──────────────────────────────────────────────────── */

/**
 * One form for create and edit, like the contacts form. A create POSTs every
 * field; an edit PATCHes ONLY what changed, so two people editing different fields
 * of the same contract do not overwrite each other.
 *
 * The document is uploaded FIRST (mint → PUT), and only then is the contract row
 * written with the returned path. A failed upload therefore writes nothing, rather
 * than leaving a contract that claims a document it does not have.
 */
function ContractForm({
  contract,
  vendorUid,
  onCancel,
  onSaved,
  testId,
}: {
  contract?: VendorContract;
  vendorUid: string;
  onCancel: () => void;
  onSaved: () => void;
  testId: string;
}) {
  const editing = contract != null;
  const [title, setTitle] = useState(contract?.title ?? '');
  const [contractType, setContractType] = useState<
    VendorContract['contract_type']
  >(contract?.contract_type ?? 'corporate');
  // A new contract starts as Corporate for one customer, and choosing TMC
  // flips it to Christopherson wide: the two fields describe the same deal, so
  // their defaults agree. Either can still be changed by hand, and an edit
  // never moves the scope on its own.
  const [wide, setWide] = useState(
    contract ? contract.customer_id === null : false,
  );
  const [customerId, setCustomerId] = useState(contract?.customer_id ?? '');
  const [programCode, setProgramCode] = useState(contract?.program_code ?? '');
  const [discount, setDiscount] = useState(contract?.discount_pct ?? '');
  const [startDate, setStartDate] = useState(contract?.start_date ?? '');
  const [endDate, setEndDate] = useState(contract?.end_date ?? '');
  const [status, setStatus] = useState<VendorContract['status']>(
    contract?.status ?? 'pending',
  );
  const [terms, setTerms] = useState(contract?.terms ?? '');
  const [notes, setNotes] = useState(contract?.notes ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    const trimmedTitle = title.trim();
    if (trimmedTitle === '') {
      setError('A contract needs a title.');
      return;
    }
    if (!wide && customerId === '') {
      setError(`Pick a customer, or choose ${CHRISTOPHERSON_WIDE}.`);
      return;
    }
    // Checked here as well as on the server, so the reader is told before a
    // 40 MB upload rather than after it.
    if (startDate && endDate && endDate < startDate) {
      setError('The end date is before the start date.');
      return;
    }
    if (file && file.size > MAX_FILE_BYTES) {
      setError('That file is over the 50 MB limit.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let docBlobPath: string | null = null;
      if (file) {
        setProgress('Uploading document…');
        const mint = await apiFetch<{ upload_url: string; blob_path: string }>(
          '/api/btp/vendor-contracts/upload',
          {
            method: 'POST',
            body: JSON.stringify({
              vendor_uid: vendorUid,
              filename: file.name,
              size_bytes: file.size,
            }),
          },
        );
        // No bearer: the SAS in the URL authorizes the PUT.
        const put = await fetch(mint.upload_url, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        });
        if (!put.ok) throw new Error(`Upload failed (${put.status})`);
        docBlobPath = mint.blob_path;
      }

      setProgress('Saving contract…');
      const fields = {
        title: trimmedTitle,
        contract_type: contractType,
        customer_id: wide ? null : customerId,
        program_code: programCode.trim(),
        discount_pct: String(discount).trim(),
        start_date: startDate,
        end_date: endDate,
        status,
        terms: terms.trim(),
        notes: notes.trim(),
      };

      if (editing) {
        const before: Record<string, unknown> = {
          title: contract.title,
          contract_type: contract.contract_type,
          customer_id: contract.customer_id,
          program_code: contract.program_code ?? '',
          discount_pct: contract.discount_pct ?? '',
          start_date: contract.start_date ?? '',
          end_date: contract.end_date ?? '',
          status: contract.status,
          terms: contract.terms ?? '',
          notes: contract.notes ?? '',
        };
        const patch: Record<string, unknown> = { id: contract.id };
        for (const [k, v] of Object.entries(fields)) {
          if (v !== before[k]) patch[k] = v;
        }
        if (docBlobPath) patch.doc_blob_path = docBlobPath;
        // Nothing changed: close rather than send an empty edit the server
        // would refuse, and that would write an audit row for nothing.
        if (Object.keys(patch).length === 1) {
          onCancel();
          return;
        }
        await apiFetch('/api/btp/vendor-contracts', {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
      } else {
        await apiFetch('/api/btp/vendor-contracts', {
          method: 'POST',
          body: JSON.stringify({
            vendor_uid: vendorUid,
            ...fields,
            ...(docBlobPath ? { doc_blob_path: docBlobPath } : {}),
          }),
        });
      }
      onSaved();
    } catch (err) {
      // The server's sentence, verbatim — it names the field and the rule.
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const label = (text: string) => (
    <span style={{ fontSize: 11, fontWeight: 600 }}>{text}</span>
  );

  return (
    <form
      data-testid={testId}
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 10,
        marginTop: 10,
        padding: 12,
        border: '1px solid var(--border-light)',
        borderRadius: 4,
      }}
    >
      <div className="detail-grid" style={{ gap: '8px 14px' }}>
        <label style={{ display: 'grid', gap: 3 }}>
          {label('Title')}
          <input
            className="input"
            data-testid={`${testId}-title`}
            disabled={saving}
            onChange={(e) => setTitle(e.target.value)}
            required
            value={title}
          />
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          {label('Type')}
          <select
            className="select"
            data-testid={`${testId}-type`}
            disabled={saving}
            onChange={(e) => {
              const v = e.target.value as VendorContract['contract_type'];
              setContractType(v);
              if (!editing) setWide(v === 'tmc');
            }}
            value={contractType}
          >
            {Object.entries(CONTRACT_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          {label('Status')}
          <select
            className="select"
            data-testid={`${testId}-status`}
            disabled={saving}
            onChange={(e) =>
              setStatus(e.target.value as VendorContract['status'])
            }
            value={status}
          >
            {Object.entries(CONTRACT_STATUS_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          {label('Program code')}
          <input
            className="input mono"
            data-testid={`${testId}-program-code`}
            disabled={saving}
            onChange={(e) => setProgramCode(e.target.value)}
            title="Tour code, ticket designator, CDP or discount code — whatever the vendor calls it"
            value={programCode}
          />
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          {label('Discount %')}
          <input
            className="input"
            data-testid={`${testId}-discount`}
            disabled={saving}
            inputMode="decimal"
            onChange={(e) => setDiscount(e.target.value)}
            value={discount}
          />
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          {label('Start date')}
          <input
            className="input"
            data-testid={`${testId}-start`}
            disabled={saving}
            onChange={(e) => setStartDate(e.target.value)}
            type="date"
            value={startDate}
          />
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          {label('End date')}
          <input
            className="input"
            data-testid={`${testId}-end`}
            disabled={saving}
            onChange={(e) => setEndDate(e.target.value)}
            type="date"
            value={endDate}
          />
        </label>
      </div>

      <fieldset
        disabled={saving}
        style={{ border: 0, margin: 0, padding: 0, display: 'grid', gap: 6 }}
      >
        <legend style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
          Applies to
        </legend>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            checked={wide}
            data-testid={`${testId}-scope-wide`}
            name={`${testId}-scope`}
            onChange={() => setWide(true)}
            type="radio"
          />
          <span style={{ fontSize: 12 }}>
            {CHRISTOPHERSON_WIDE} — a TMC-level deal no single customer owns
          </span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            checked={!wide}
            data-testid={`${testId}-scope-customer`}
            name={`${testId}-scope`}
            onChange={() => setWide(false)}
            type="radio"
          />
          <span style={{ fontSize: 12 }}>One customer</span>
        </label>
        {!wide && (
          <CustomerSearchSelect
            onChange={setCustomerId}
            testId={`${testId}-customer`}
            value={customerId}
            valueName={contract?.customer_name}
          />
        )}
      </fieldset>

      <label style={{ display: 'grid', gap: 3 }}>
        {label('Terms')}
        <textarea
          className="textarea"
          data-testid={`${testId}-terms`}
          disabled={saving}
          onChange={(e) => setTerms(e.target.value)}
          rows={3}
          value={terms}
        />
      </label>
      <label style={{ display: 'grid', gap: 3 }}>
        {label('Notes')}
        <textarea
          className="textarea"
          data-testid={`${testId}-notes`}
          disabled={saving}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          value={notes}
        />
      </label>
      <label style={{ display: 'grid', gap: 3 }}>
        {label(
          editing && contract.has_document
            ? 'Replace contract PDF (optional, up to 50 MB)'
            : 'Contract PDF (optional, up to 50 MB)',
        )}
        <input
          accept="application/pdf,.pdf"
          data-testid={`${testId}-file`}
          disabled={saving}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          type="file"
        />
      </label>

      {error && (
        <p
          className="detail-note"
          data-testid={`${testId}-error`}
          role="alert"
          style={{ margin: 0 }}
        >
          {error}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          className="btn btn-primary btn-sm"
          data-testid={`${testId}-save`}
          disabled={saving || title.trim() === ''}
          type="submit"
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Add contract'}
        </button>
        <button
          className="btn btn-sm"
          data-testid={`${testId}-cancel`}
          disabled={saving}
          onClick={onCancel}
          type="button"
        >
          Cancel
        </button>
        {progress && (
          <span className="muted" style={{ fontSize: 12 }}>
            {progress}
          </span>
        )}
      </div>
    </form>
  );
}
